// Backtracking — Basic vs Optimized code for every "Beginner Problems" drill listed in the
// Backtracking Explanation modal. Same shape as heapDrills.js: concise steps + an interactive
// "watch it happen" visualizer per version, reusing ApproachVisualizer's array-row support.
// Two drills cross-reference the curated problems instead of duplicating content.

const backtrackingDrills = [
  {
    id: 'generate-all-permutations',
    title: 'Generate All Permutations of Distinct Numbers',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Remove-and-Recurse',
        explanation: 'For each candidate, remove it from the list, recursively find all permutations of what remains, and prepend the removed value to each. Correct and readable, but building a new shortened list at every single call adds real overhead.',
        steps: [
          'If the list is empty, the only permutation is the empty one — base case.',
          'For each candidate in the list, build a new list with that one candidate removed.',
          'Recursively find every permutation of that shorter list.',
          'Prepend the removed candidate to each of those, and collect the results — a new list is allocated at every call.',
        ],
        code:
`List<List<Integer>> permuteBasic(List<Integer> nums) {
    if (nums.isEmpty()) {
        List<List<Integer>> result = new ArrayList<>();
        result.add(new ArrayList<>());
        return result;
    }
    List<List<Integer>> result = new ArrayList<>();
    for (int i = 0; i < nums.size(); i++) {
        int chosen = nums.get(i);
        List<Integer> remaining = new ArrayList<>(nums);
        remaining.remove(i);   // O(n) shift, and a whole new list built
        for (List<Integer> perm : permuteBasic(remaining)) {
            perm.add(0, chosen);
            result.add(perm);
        }
    }
    return result;
}`,
        complexity: { time: 'O(n · n!)', space: 'O(n · n!) — a new list built at every recursive call' },
      },
      {
        name: 'Optimized — In-Place Swapping',
        explanation: 'Instead of building new lists, swap elements directly within the ORIGINAL array. At each position, try every remaining candidate by swapping it into that position, recurse, then swap back — restoring the array so the next candidate gets a fair, unmodified starting point.',
        steps: [
          'Recurse with a position index. If it reaches the array\'s length, the array\'s current arrangement is one complete permutation — record it.',
          'For each index from the current position onward, swap it into the current position.',
          'Recurse to fill the next position.',
          'Swap back (undo) before trying the next candidate — no new lists ever allocated.',
        ],
        visual: {
          arrays: [{ label: 'nums', values: [1, 2, 3] }],
          varNames: [],
          steps: [
            { highlights: { nums: [0] }, note: 'Position 0: try index 0 (swap with itself — no-op). Recurse into position 1.' },
            { highlights: { nums: [1] }, note: 'Position 1: try index 1 (no-op). Recurse into position 2 — full length reached. Record [1, 2, 3].' },
            { snapshots: { nums: [1, 3, 2] }, highlights: { nums: [1, 2] }, note: 'Back at position 1: swap index 1 and 2 → [1, 3, 2]. Recurse — record [1, 3, 2]. Then swap back to restore [1, 2, 3].' },
            { snapshots: { nums: [2, 1, 3] }, highlights: { nums: [0, 1] }, note: 'Back at position 0: swap index 0 and 1 → [2, 1, 3]. Recurse into position 1 with this new arrangement — continues the same way, eventually finding [2,1,3] and [2,3,1], then swaps back and tries index 2, finding [3,2,1] and [3,1,2]. Full set of 6 permutations found, no extra lists ever built.' },
          ],
        },
        code:
`void permute(int[] nums, int pos, List<List<Integer>> result) {
    if (pos == nums.length) {
        List<Integer> perm = new ArrayList<>();
        for (int n : nums) perm.add(n);
        result.add(perm);
        return;
    }
    for (int i = pos; i < nums.length; i++) {
        swap(nums, pos, i);
        permute(nums, pos + 1, result);
        swap(nums, pos, i);   // swap back
    }
}

private void swap(int[] nums, int i, int j) {
    int temp = nums[i]; nums[i] = nums[j]; nums[j] = temp;
}`,
        complexity: { time: 'O(n · n!)', space: 'O(n) — recursion depth, no extra lists per call' },
      },
    ],
  },
  {
    id: 'generate-all-combinations',
    title: 'Generate All Combinations of Size K',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Generate Every Subset, Then Filter by Size',
        explanation: 'Generate every possible subset of the input (all 2ⁿ of them), then keep only the ones with exactly k elements. Correct, but the vast majority of generated subsets get thrown away immediately.',
        steps: [
          'Generate every subset of the input, using the standard subsets backtracking.',
          'Filter that full list down to only the subsets with exactly k elements.',
          'Return the filtered list.',
          'For most inputs, only a small fraction of the 2ⁿ subsets have the right size — the rest were wasted work.',
        ],
        code:
`List<List<Integer>> combineBasic(int n, int k) {
    List<List<Integer>> allSubsets = new ArrayList<>();
    backtrackAllSubsets(1, n, new ArrayList<>(), allSubsets);

    List<List<Integer>> result = new ArrayList<>();
    for (List<Integer> subset : allSubsets) {
        if (subset.size() == k) result.add(subset);
    }
    return result;
}

private void backtrackAllSubsets(int start, int n, List<Integer> path, List<List<Integer>> result) {
    result.add(new ArrayList<>(path));
    for (int i = start; i <= n; i++) {
        path.add(i);
        backtrackAllSubsets(i + 1, n, path, result);
        path.remove(path.size() - 1);
    }
}`,
        complexity: { time: 'O(2ⁿ)', space: 'O(2ⁿ) — every subset built before filtering' },
      },
      {
        name: 'Optimized — Backtrack With a Feasibility Prune',
        explanation: 'Only ever build combinations of exactly size k in the first place — never generate a wrong-sized one at all. Additionally, prune immediately whenever there simply aren\'t enough numbers left to reach size k, even if every remaining candidate were chosen.',
        steps: [
          'Stop and record the moment the path reaches exactly k elements — never grow it further.',
          'Before trying a candidate, check: does enough of the range remain to still reach size k? If not, stop this loop early — every later candidate would fail the same check.',
          'Choose a candidate, recurse, then un-choose.',
          'No subset of the wrong size is ever fully constructed.',
        ],
        visual: {
          arrays: [{ label: 'range 1..4 (k = 2)', values: [1, 2, 3, 4] }],
          varNames: [],
          steps: [
            { highlights: { 'range 1..4 (k = 2)': [0] }, note: 'Choose 1. Path: [1]. Need 1 more from {2,3,4} — plenty remain, continue.' },
            { highlights: { 'range 1..4 (k = 2)': [0, 1] }, note: 'Choose 2. Path: [1,2] — size 2 reached! Record [1,2]. Backtrack.' },
            { highlights: { 'range 1..4 (k = 2)': [0, 2] }, note: 'Backtrack, choose 3 instead. Path: [1,3] — record. Backtrack, choose 4: path [1,4] — record. Backtrack out of 1 entirely.' },
            { highlights: { 'range 1..4 (k = 2)': [1] }, note: 'Choose 2 as the first element. Continue similarly: [2,3] and [2,4] get recorded. Then choose 3 first: only [3,4] remains possible (choosing 4 first would leave nothing after it — the feasibility check prunes that branch instantly). Final: [1,2],[1,3],[1,4],[2,3],[2,4],[3,4] — all 6 combinations, no wrong-sized ones ever built.' },
          ],
        },
        code:
`List<List<Integer>> combine(int n, int k) {
    List<List<Integer>> result = new ArrayList<>();
    backtrack(1, n, k, new ArrayList<>(), result);
    return result;
}

private void backtrack(int start, int n, int k, List<Integer> path, List<List<Integer>> result) {
    if (path.size() == k) {
        result.add(new ArrayList<>(path));
        return;
    }
    int needed = k - path.size();
    for (int i = start; i <= n - needed + 1; i++) {   // prune: not enough numbers left past here
        path.add(i);
        backtrack(i + 1, n, k, path, result);
        path.remove(path.size() - 1);
    }
}`,
        complexity: { time: 'O(k · C(n, k))', space: 'O(k) — recursion depth' },
      },
    ],
  },
  {
    id: 'subsets-drill',
    title: 'Generate All Subsets',
    level: 'Easy',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Subsets\" there for the complete step-by-step and both code versions. You can also try it hands-on in " +
      "the \"Subsets\" mode of the Interactive Practice playground with any small set of your choosing.",
  },
  {
    id: 'combination-sum-drill',
    title: 'Combination Sum (Reuse Allowed)',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Generate Every Possible Combination, Then Filter',
        explanation: 'Generate combinations of candidates (allowing repeats, up to some bound on how many times a number can repeat) and check each one\'s sum against the target afterward. Correct in spirit, but without pruning, the number of combinations to even consider explodes quickly, and most get discarded.',
        steps: [
          'Generate combinations of candidates, allowing repeats, up to a bound on total length.',
          'For each fully-built combination, sum it and check against the target.',
          'Keep the ones that match exactly.',
          'The vast majority of generated combinations are discarded — their sums are checked only after being fully built.',
        ],
        code:
`List<List<Integer>> combinationSumBasic(int[] candidates, int target, int maxLen) {
    List<List<Integer>> all = new ArrayList<>();
    generateAll(candidates, maxLen, new ArrayList<>(), all);

    List<List<Integer>> result = new ArrayList<>();
    for (List<Integer> combo : all) {
        int sum = combo.stream().mapToInt(Integer::intValue).sum();
        if (sum == target) result.add(combo);
    }
    return result;
}

private void generateAll(int[] candidates, int remainingLen, List<Integer> path, List<List<Integer>> all) {
    all.add(new ArrayList<>(path));
    if (remainingLen == 0) return;
    for (int c : candidates) {
        path.add(c);
        generateAll(candidates, remainingLen - 1, path, all);
        path.remove(path.size() - 1);
    }
}`,
        complexity: { time: 'Exponential in maxLen — grows uncontrolled without a sum-based bound', space: 'Exponential' },
      },
      {
        name: 'Optimized — Backtrack With an Early Sum Prune',
        explanation: 'Track the running sum as the path is built, and prune the INSTANT adding a candidate would push the sum over the target — never even recursing into that doomed branch. Reusing the same start index (not start + 1) allows a candidate to be picked again, exactly matching the "reuse allowed" rule.',
        steps: [
          'If the running sum exactly equals the target, the current path is a valid combination — record it.',
          'For each candidate from the current start index onward, check: would adding it exceed the target? If so, skip it immediately — no recursion.',
          'Otherwise, choose it, recurse with the SAME start index (allowing reuse), then un-choose.',
          'Every branch that would exceed the target is cut off the moment it\'s considered, not after being fully built.',
        ],
        visual: {
          arrays: [{ label: 'candidates (target = 7)', values: [2, 3, 6, 7] }],
          varNames: ['sum'],
          steps: [
            { highlights: { 'candidates (target = 7)': [0] }, vars: { sum: 2 }, note: 'Choose 2. Sum = 2. Still room — continue choosing from index 0 onward (2 can repeat).' },
            { highlights: { 'candidates (target = 7)': [0] }, vars: { sum: 4 }, note: 'Choose 2 again. Sum = 4. Continue.' },
            { highlights: { 'candidates (target = 7)': [0] }, vars: { sum: 6 }, note: 'Choose 2 again. Sum = 6. Adding 2 again would make 8 — over target. Try the next candidate instead.' },
            { highlights: { 'candidates (target = 7)': [1] }, vars: { sum: 7 }, note: 'Choose 3. Sum = 7 — exactly the target! Record [2, 2, 3]. Backtrack and explore other branches (eventually finding [7] too).' },
          ],
        },
        code:
`List<List<Integer>> combinationSum(int[] candidates, int target) {
    List<List<Integer>> result = new ArrayList<>();
    backtrack(candidates, target, 0, 0, new ArrayList<>(), result);
    return result;
}

private void backtrack(int[] candidates, int target, int start, int sum, List<Integer> path, List<List<Integer>> result) {
    if (sum == target) {
        result.add(new ArrayList<>(path));
        return;
    }
    for (int i = start; i < candidates.length; i++) {
        if (sum + candidates[i] > target) continue;   // PRUNE
        path.add(candidates[i]);
        backtrack(candidates, target, i, sum + candidates[i], path, result);   // i, not i+1
        path.remove(path.size() - 1);
    }
}`,
        complexity: { time: 'O(candidates^(target / minCandidate))', space: 'O(target / minCandidate) — recursion depth' },
      },
    ],
  },
  {
    id: 'generate-parentheses',
    title: 'Generate All Valid Combinations of Parentheses',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Generate Every String, Then Filter',
        explanation: 'Generate every possible string of length 2n made of \'(\' and \')\' characters, then check each one for validity afterward with a separate balance-checking pass. Correct, but the vast majority of generated strings — anything unbalanced — get built and then thrown away.',
        steps: [
          'Generate every possible string of length 2n over the alphabet {(, )}.',
          'For each one, scan it to check whether parentheses are properly balanced.',
          'Keep only the valid ones.',
          'Out of 2^(2n) total strings generated, only the Catalan-number few are ever valid.',
        ],
        code:
`List<String> generateParenthesisBasic(int n) {
    List<String> all = new ArrayList<>();
    generateAll(new StringBuilder(), 2 * n, all);
    List<String> result = new ArrayList<>();
    for (String s : all) {
        if (isValid(s)) result.add(s);
    }
    return result;
}

private void generateAll(StringBuilder sb, int remaining, List<String> all) {
    if (remaining == 0) { all.add(sb.toString()); return; }
    sb.append('('); generateAll(sb, remaining - 1, all); sb.deleteCharAt(sb.length() - 1);
    sb.append(')'); generateAll(sb, remaining - 1, all); sb.deleteCharAt(sb.length() - 1);
}

private boolean isValid(String s) {
    int balance = 0;
    for (char c : s.toCharArray()) {
        balance += (c == '(') ? 1 : -1;
        if (balance < 0) return false;
    }
    return balance == 0;
}`,
        complexity: { time: 'O(2^(2n) · n)', space: 'O(2^(2n) · n)' },
      },
      {
        name: 'Optimized — Only Add a Character That Keeps the String Valid',
        explanation: 'Track how many open and close parentheses have been used so far, and only ever place a character that CAN\'T immediately break validity: an open paren if fewer than n have been used, or a close paren only if there\'s currently an unmatched open one available. Every string built this way is automatically valid — no filtering needed at all.',
        steps: [
          'Track counts of open and close parentheses placed so far.',
          'If opens < n, placing another \'(\' is always safe — try it.',
          'If closes < opens, placing a \')\' is safe (there\'s an unmatched open to close) — try it.',
          'When the string reaches length 2n, it\'s guaranteed valid — record it directly.',
        ],
        visual: {
          arrays: [{ label: 'path (n = 3)', values: [] }],
          varNames: ['opens', 'closes'],
          steps: [
            { snapshots: { path: ['('] }, vars: { opens: 1, closes: 0 }, note: 'opens(0) < n(3) — place \'(\' (this is always tried before \')\' at each step).' },
            { snapshots: { path: ['(', '('] }, vars: { opens: 2, closes: 0 }, note: 'opens(1) < 3 — place \'(\' again.' },
            { snapshots: { path: ['(', '(', '('] }, vars: { opens: 3, closes: 0 }, note: 'opens(2) < 3 — place \'(\' a third time. Now opens=3, so no more \'(\' is allowed anywhere in this branch.' },
            { snapshots: { path: ['(', '(', '(', ')'] }, vars: { opens: 3, closes: 1 }, note: 'opens(3) is no longer < 3, so \')\' is the only option: closes(0) < opens(3) — place \')\'. Continuing this way eventually records "((()))" , then backtracks to try every other valid arrangement.' },
          ],
        },
        code:
`List<String> generateParenthesis(int n) {
    List<String> result = new ArrayList<>();
    backtrack(new StringBuilder(), 0, 0, n, result);
    return result;
}

private void backtrack(StringBuilder path, int opens, int closes, int n, List<String> result) {
    if (path.length() == 2 * n) {
        result.add(path.toString());
        return;
    }
    if (opens < n) {
        path.append('(');
        backtrack(path, opens + 1, closes, n, result);
        path.deleteCharAt(path.length() - 1);
    }
    if (closes < opens) {
        path.append(')');
        backtrack(path, opens, closes + 1, n, result);
        path.deleteCharAt(path.length() - 1);
    }
}`,
        complexity: { time: 'O(4ⁿ / √n) — the nth Catalan number, tightly bounded', space: 'O(n) — recursion depth' },
      },
    ],
  },
  {
    id: 'letter-combinations-phone-number',
    title: 'Letter Combinations of a Phone Number',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Expand One Digit at a Time, Holding Every Partial Result',
        explanation: 'Start with a list containing just the empty string. For each digit, build a brand-new list by combining every existing partial string with every letter that digit maps to. Correct, but holds the ENTIRE growing set of partial combinations in memory simultaneously at every stage.',
        steps: [
          'Start with a list containing one entry: the empty string.',
          'For each digit, build a new list: for every string already in the list, append every letter that digit could map to.',
          'Replace the old list with this new, longer one.',
          'After all digits are processed, the final list holds every combination — but every intermediate stage held a full copy of all partial results.',
        ],
        code:
`List<String> letterCombinationsBasic(String digits) {
    if (digits.isEmpty()) return new ArrayList<>();
    String[] mapping = { "", "", "abc", "def", "ghi", "jkl", "mno", "pqrs", "tuv", "wxyz" };

    List<String> result = new ArrayList<>();
    result.add("");
    for (char digit : digits.toCharArray()) {
        List<String> next = new ArrayList<>();
        for (String partial : result) {
            for (char letter : mapping[digit - '0'].toCharArray()) {
                next.add(partial + letter);
            }
        }
        result = next;   // an entirely new list, every stage
    }
    return result;
}`,
        complexity: { time: 'O(4ⁿ · n)', space: 'O(4ⁿ) — every intermediate stage\'s full list kept in memory' },
      },
      {
        name: 'Optimized — Backtrack, Building One String at a Time',
        explanation: 'Build a single path character by character using a StringBuilder, appending one digit\'s letter, recursing to the next digit, then removing it before trying the next letter. Only ONE partial string exists in memory at any moment — the rest of the exploration lives on the call stack, not in a growing list.',
        steps: [
          'If the path\'s length equals the number of digits, it\'s a complete combination — record it.',
          'Otherwise, look up the letters mapped to the current digit.',
          'For each letter, append it to the path, recurse to the next digit, then remove it.',
          'Only one string is ever "in progress" at a time.',
        ],
        visual: {
          arrays: [{ label: 'path (digits = "23")', values: [] }],
          varNames: [],
          steps: [
            { snapshots: { path: ['a'] }, note: 'Digit \'2\' maps to "abc" — try \'a\' first. Path: "a".' },
            { snapshots: { path: ['a', 'd'] }, note: 'Digit \'3\' maps to "def" — try \'d\'. Path: "ad" — full length, record it.' },
            { snapshots: { path: ['a', 'e'] }, note: 'Backtrack, remove \'d\', try \'e\': record "ae". Then \'f\': record "af". Backtrack out of digit 2\'s \'a\'.' },
            { snapshots: { path: ['b'] }, note: 'Try \'b\' for digit \'2\': same three-letter expansion gives "bd", "be", "bf". Then \'c\' gives "cd", "ce", "cf". Final: 9 combinations, one string built and torn down at a time.' },
          ],
        },
        code:
`List<String> letterCombinations(String digits) {
    List<String> result = new ArrayList<>();
    if (digits.isEmpty()) return result;
    String[] mapping = { "", "", "abc", "def", "ghi", "jkl", "mno", "pqrs", "tuv", "wxyz" };
    backtrack(digits, 0, new StringBuilder(), mapping, result);
    return result;
}

private void backtrack(String digits, int index, StringBuilder path, String[] mapping, List<String> result) {
    if (index == digits.length()) {
        result.add(path.toString());
        return;
    }
    String letters = mapping[digits.charAt(index) - '0'];
    for (char letter : letters.toCharArray()) {
        path.append(letter);
        backtrack(digits, index + 1, path, mapping, result);
        path.deleteCharAt(path.length() - 1);
    }
}`,
        complexity: { time: 'O(4ⁿ · n)', space: 'O(n) — one path in progress at a time, plus the output' },
      },
    ],
  },
  {
    id: 'n-queens-drill',
    title: 'N-Queens',
    level: 'Advanced',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"N-Queens\" there for the complete step-by-step and both code versions. You can also try it hands-on in " +
      "the \"N-Queens\" mode of the Interactive Practice playground with any board size from 4 to 8.",
  },
  {
    id: 'palindrome-partitioning',
    title: 'Palindrome Partitioning',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Generate Every Partition, Then Filter',
        explanation: 'A string can be split into substrings at any subset of the gaps between characters — generate every possible way of doing that, then check whether EVERY resulting substring happens to be a palindrome. Correct, but most generated partitions get fully built before being discarded for having even one non-palindromic piece.',
        steps: [
          'Generate every possible partition of the string (a choice of cut-or-no-cut at each gap between characters).',
          'For each complete partition, check whether every substring in it is a palindrome.',
          'Keep only the partitions where all substrings qualify.',
          'A partition with an obviously non-palindromic first piece still gets fully built before being discarded.',
        ],
        code:
`List<List<String>> partitionBasic(String s) {
    List<List<String>> all = generateAllPartitions(s);
    List<List<String>> result = new ArrayList<>();
    for (List<String> partition : all) {
        if (partition.stream().allMatch(this::isPalindrome)) {
            result.add(partition);
        }
    }
    return result;
}

private boolean isPalindrome(String s) {
    int lo = 0, hi = s.length() - 1;
    while (lo < hi) {
        if (s.charAt(lo++) != s.charAt(hi--)) return false;
    }
    return true;
}
// generateAllPartitions(s): recursively tries every cut point, omitted here for brevity.`,
        complexity: { time: 'O(2ⁿ · n) — every partition fully built, then checked', space: 'O(2ⁿ · n)' },
      },
      {
        name: 'Optimized — Only Extend With a Verified Palindrome',
        explanation: 'At each position, only try substrings starting there that are ALREADY confirmed palindromes — check before adding, not after building the whole partition. A branch that would introduce a non-palindromic piece is never even recursed into.',
        steps: [
          'From the current starting position, try every possible end position for the next piece.',
          'Check whether that specific substring is a palindrome BEFORE adding it to the path.',
          'If it is, add it, recurse from the position right after it, then remove it (backtrack).',
          'If it isn\'t a palindrome, skip it immediately — no recursion, no wasted exploration into that branch.',
        ],
        visual: {
          arrays: [{ label: 's = "aab"', values: ['a', 'a', 'b'] }],
          varNames: [],
          steps: [
            { highlights: { 's = "aab"': [0] }, note: 'From position 0, try "a" (indices 0-0): a palindrome — choose it. Path: ["a"].' },
            { highlights: { 's = "aab"': [1] }, note: 'From position 1, try "a" (index 1-1): a palindrome — choose it. Path: ["a", "a"].' },
            { highlights: { 's = "aab"': [2] }, note: 'From position 2, try "b": a palindrome — choose it. Path: ["a", "a", "b"] — reached the end. Record it.' },
            { highlights: { 's = "aab"': [0, 1] }, note: 'Backtrack fully. From position 0, try "aa" (indices 0-1): also a palindrome — choose it. Path: ["aa"].' },
            { highlights: { 's = "aab"': [2] }, note: 'From position 2, try "b": a palindrome — choose it. Path: ["aa", "b"] — reached the end. Record it. Final: [["a","a","b"], ["aa","b"]] — "ab" (indices 0-2) was never even tried, since it\'s not a palindrome.' },
          ],
        },
        code:
`List<List<String>> partition(String s) {
    List<List<String>> result = new ArrayList<>();
    backtrack(s, 0, new ArrayList<>(), result);
    return result;
}

private void backtrack(String s, int start, List<String> path, List<List<String>> result) {
    if (start == s.length()) {
        result.add(new ArrayList<>(path));
        return;
    }
    for (int end = start; end < s.length(); end++) {
        if (!isPalindrome(s, start, end)) continue;   // skip immediately, no recursion
        path.add(s.substring(start, end + 1));
        backtrack(s, end + 1, path, result);
        path.remove(path.size() - 1);
    }
}

private boolean isPalindrome(String s, int lo, int hi) {
    while (lo < hi) {
        if (s.charAt(lo++) != s.charAt(hi--)) return false;
    }
    return true;
}`,
        complexity: { time: 'O(2ⁿ · n) worst case, far better in practice due to pruning', space: 'O(n) — recursion depth' },
      },
    ],
  },
  {
    id: 'rat-in-a-maze',
    title: 'Find a Path Through a Maze (Rat in a Maze)',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — No Visited Tracking',
        explanation: 'Try moving in every direction from the current cell, recursively, without remembering which cells have already been visited on the current path. This risks looping back and forth between the same cells forever — needs an artificial depth cap just to guarantee it terminates for this demonstration.',
        steps: [
          'From the current cell, try moving in each of the 4 directions.',
          'A move is allowed if the destination is in bounds and not blocked.',
          'Recurse into that cell — but nothing stops the search from immediately moving right back where it came from.',
          'Without a depth cap, this can recurse forever, bouncing between two open adjacent cells.',
        ],
        code:
`// A depth cap is added ONLY to keep this demonstration from looping forever —
// real code needs actual visited-tracking, which the optimized version provides.
boolean hasPathBasic(int[][] grid, int r, int c, int depth) {
    int n = grid.length;
    if (r == n - 1 && c == n - 1) return true;
    if (depth <= 0) return false;   // artificial cutoff, not a real solution
    int[][] dirs = { {1,0}, {-1,0}, {0,1}, {0,-1} };
    for (int[] d : dirs) {
        int nr = r + d[0], nc = c + d[1];
        if (nr >= 0 && nr < n && nc >= 0 && nc < n && grid[nr][nc] == 0) {
            if (hasPathBasic(grid, nr, nc, depth - 1)) return true;
        }
    }
    return false;
}`,
        complexity: { time: 'Exponential, and can loop forever without an artificial cap', space: 'O(depth cap)' },
      },
      {
        name: 'Optimized — Mark Visited on Entry, Unmark on Backtrack',
        explanation: 'Mark each cell as visited the moment the path enters it, so the search never immediately doubles back. Un-mark it again on backtrack — a cell not part of the CURRENT path might still be a valid part of a different path explored later.',
        steps: [
          'Mark the current cell visited before trying any moves from it.',
          'Try each of the 4 directions — a move is allowed only if in bounds, not blocked, AND not already visited on this path.',
          'Recurse; if any direction reaches the target, propagate success back up immediately.',
          'Before returning (success or failure), un-mark the current cell — future, different paths may still need to pass through it.',
        ],
        visual: {
          arrays: [
            { label: 'row 0', values: [0, 0, 0] },
            { label: 'row 1', values: [0, 0, 0] },
            { label: 'row 2', values: [0, 0, 0] },
          ],
          varNames: [],
          steps: [
            { highlights: { 'row 0': [0] }, note: 'Start at (0,0). Mark visited. Try right → (0,1).' },
            { highlights: { 'row 0': [1] }, note: '(0,1) is open and unvisited — mark it, move there. Try right → (0,2).' },
            { highlights: { 'row 0': [2] }, note: '(0,2) is open and unvisited — mark it. Try right (out of bounds), try down → (1,2).' },
            { highlights: { 'row 1': [2] }, note: '(1,2) is open and unvisited — mark it. Try down → (2,2), the target!' },
            { highlights: { 'row 2': [2] }, note: '(2,2) is the target — path found: (0,0)→(0,1)→(0,2)→(1,2)→(2,2). Success propagates back up through every call — no cell needs unmarking since the very first attempt succeeded.' },
          ],
        },
        code:
`boolean hasPath(int[][] grid, int r, int c, boolean[][] visited) {
    int n = grid.length;
    if (r == n - 1 && c == n - 1) return true;

    visited[r][c] = true;
    int[][] dirs = { {1,0}, {-1,0}, {0,1}, {0,-1} };
    for (int[] d : dirs) {
        int nr = r + d[0], nc = c + d[1];
        if (nr >= 0 && nr < n && nc >= 0 && nc < n
                && grid[nr][nc] == 0 && !visited[nr][nc]) {
            if (hasPath(grid, nr, nc, visited)) return true;
        }
    }
    visited[r][c] = false;   // un-mark — a different path may still need this cell
    return false;
}`,
        complexity: { time: 'O(4^(rows·cols)) worst case, far better in practice — visited-tracking prevents true infinite loops and most redundant re-exploration', space: 'O(rows · cols) — the visited grid, plus recursion depth' },
      },
    ],
  },
];

export default backtrackingDrills;
